import { alpha, Box, Button, TextField, Typography } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { LoadingButton } from "src/shared/components";
import { buttons, inputs } from "src/shared/constants";
import { setUserAuthentication } from "src/shared/lib/store/slices/auth";
import { useTypedDispatch } from "src/shared/lib/store/store";
import {
  getRuleSizeAndLength,
  regexps,
  messages,
} from "../../../shared/utils/hookFormRules";
import {
  useLazyResendSmsQuery,
  useLazySend2EmailQuery,
  useLazySendCodeQuery,
} from "../model/api/signIn";
import { IBlurMethod, ICodeConfirm } from "../model/types/types";

export const SignInConfirm: FC<IBlurMethod> = ({ handleFocus }) => {
  const [isShowBtns, setIsShowBtns] = useState(false);
  const [seconds, setSeconds] = useState(120);
  const [errorMessage, setErrorMessage] = useState("");

  const dispatch = useTypedDispatch();
  const navigate = useNavigate();

  const [sendCode, { isFetching }] = useLazySendCodeQuery();
  const [resendSms] = useLazyResendSmsQuery();
  const [send2Email] = useLazySend2EmailQuery();

  const clearMessageError = () => setErrorMessage("");

  const handleSendCode = async (value: { code: string }) => {
    clearMessageError();
    try {
      const { isSuccess, isError } = await sendCode(value?.code);

      if (isSuccess) {
        dispatch(setUserAuthentication(true));
        navigate("/");
      }

      if (isError) {
        setErrorMessage("Произошла ошибка");
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleResendSms = async () => {
    clearMessageError();
    try {
      const { isError: isErrorResendSms } = await resendSms();

      if (isErrorResendSms) {
        setErrorMessage("Произошла ошибка");
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleSend2Email = async () => {
    clearMessageError();
    try {
      const { isError: isErrorSend2Email } = await send2Email();

      if (isErrorSend2Email) {
        setErrorMessage("Произошла ошибка");
      }
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    if (seconds > 0) {
      setTimeout(setSeconds, 1000, seconds - 1);
    } else {
      setIsShowBtns(true);
    }
  }, [seconds]);

  const methods = useForm<ICodeConfirm>({
    defaultValues: { code: "" },
    mode: "all",
  });
  const {
    handleSubmit,
    control,
    formState: { isValid, errors },
  } = methods;

  const handleClickBtn = (fn: () => void) => {
    fn();
    setIsShowBtns(false);
    setSeconds(120);
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(handleSendCode)}
      sx={{
        zIndex: 2,
        py: 8,
        my: 16,
        mx: 16,
        borderRadius: 1,
        bgcolor: alpha("#000000", 0.68),
      }}
    >
      <Typography
        component="h5"
        variant="h5"
        color="textPrimary"
        sx={{
          textAlign: "center",
        }}
      >
        Вход в кабинет
      </Typography>
      <Box sx={{ maxWidth: "460px", px: 4, py: 4 }}>
        <Typography
          color="textPrimary"
          sx={{
            textAlign: "center",
          }}
        >
          Мы отправили SMS с 4-значным кодом подтверждения на ваш номер
        </Typography>
        <Box
          sx={(theme) => ({
            display: "flex",
            justifyContent: "space-between",
            mt: 2,
            [theme.breakpoints.down("sm")]: {
              flexDirection: "column",
              gap: "16px",
              alignItems: "center",
            },
          })}
        >
          <Controller
            control={control}
            name="code"
            render={({ field: { ref, ...field }, fieldState }) => (
              <TextField
                inputRef={ref}
                size={"small"}
                label={inputs.code}
                {...field}
                helperText={fieldState.error?.message}
                error={!!errors.code}
                onFocus={() => {
                  clearMessageError();
                  handleFocus(true);
                }}
                onBlur={() => handleFocus(false)}
              />
            )}
            rules={{
              required: messages.required,
              maxLength: getRuleSizeAndLength(4, messages.maxLength),
              minLength: getRuleSizeAndLength(4, messages.minLength),
              pattern: regexps.numericRegexp,
            }}
          />

          <LoadingButton
            type="submit"
            isLoading={isFetching}
            variant="contained"
            color="warning"
            title={buttons.confirm}
            sx={(theme) => ({
              alignSelf: "self-start",
              color: "white",
              [theme.breakpoints.down("sm")]: {
                margin: "0 auto",
              },
            })}
            disabled={!isValid}
          />
        </Box>

        {errorMessage && (
          <Typography
            sx={{
              textAlign: "center",
              color: "error.light",
            }}
          >
            {errorMessage}
          </Typography>
        )}

        <Typography
          sx={{
            textAlign: "center",
            color: "grey.200",
            fontSize: ".75rem",
            mt: 2,
          }}
        >
          {seconds
            ? `Код можно будет отправить повторно через ${seconds} секунд`
            : "Время действия кода истекло, попробуйте получить код еще раз"}
        </Typography>
      </Box>
      {isShowBtns && (
        <Box
          sx={(theme) => ({
            [theme.breakpoints.down("sm")]: {
              flexDirection: "column",
              gap: "16px",
              alignItems: "center",
            },
            display: "flex",
            justifyContent: "space-around",
          })}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
            }}
          >
            <Typography
              sx={{
                color: "grey.200",
                fontSize: ".8rem",
              }}
            >
              Не пришло SMS с кодом?
            </Typography>
            <Button
              variant="contained"
              size={"medium"}
              disabled={isFetching}
              sx={{
                alignSelf: "self-start",
                color: "grey.700",
                backgroundColor: "white",
              }}
              onClick={() => handleClickBtn(handleResendSms)}
            >
              <Typography>{buttons.resend}</Typography>
            </Button>
          </Box>

          <Button
            variant="contained"
            sx={(theme) => ({
              alignSelf: "self-end",
              color: "grey.700",
              backgroundColor: "white",
              [theme.breakpoints.down("sm")]: {
                margin: "0 auto",
              },
            })}
            onClick={() => handleClickBtn(handleSend2Email)}
            disabled={isFetching}
          >
            <Typography>{buttons.send2Email}</Typography>
          </Button>
        </Box>
      )}
    </Box>
  );
};
